import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/app/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Merci de renseigner votre email et votre mot de passe.");
      return;
    }
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Connexion réussie. Bienvenue.");
      navigate(redirectTo, { replace: true });
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground" data-testid="login-page">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-[0.2]" />
      <div className="pointer-events-none absolute -left-24 top-24 h-80 w-80 rounded-full bg-brand-blue/10 blur-3xl dark:bg-brand-gold/10" />

      <header className="relative z-10 flex items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-3" data-testid="login-home-link">
          <Logo size={36} />
          <div className="leading-tight">
            <div className="text-base font-bold">
              VITAE-PUBLICA<span className="text-[0.6em] align-top">™</span>
            </div>
            <div className="text-[11px] italic text-muted-foreground">
              Les Finances Publiques sont des Tranches de Vies
            </div>
          </div>
        </Link>
        <ThemeToggle />
      </header>

      <div className="relative z-10 mx-auto flex w-full max-w-md flex-col px-6 pt-6 pb-16 animate-fade-in-up">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-extrabold tracking-tight">Connexion</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Accédez à votre espace d'investigation budgétaire.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="rounded-xl border border-border bg-card p-6 shadow-sm"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="vous@exemple.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 pr-10"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  data-testid="login-toggle-pwd"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-1.5 text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-muted-foreground underline-offset-4 transition hover:text-foreground hover:underline"
                  data-testid="login-forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            {error && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 text-base font-semibold"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Connexion…" : "Se connecter"}
              {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </div>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Pas encore de compte ?{" "}
          <Link
            to="/register"
            className="font-semibold text-foreground underline-offset-4 hover:underline"
            data-testid="login-goto-register"
          >
            Créer un compte
          </Link>
        </p>
      </div>
    </div>
  );
}
